// pages/admin/AdminUsers.js
// ניהול משתמשים — CRUD + Reset Password
//
// API:
//   GET /users → רשימה
//   POST /users { username, password, password2?, full_name, email, is_admin, is_super_admin, view_restriction } → יצירה
//   PUT /users/{id} { username, full_name, email, is_admin, is_super_admin, view_restriction } → עדכון
//   DELETE /users/{id} → מחיקה (hard delete)
//   POST /users/{id}/reset-password { new_password, new_password_secondary? } → איפוס סיסמה
//
// Permissions tab — handled separately (response 5)

import { useState, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Snackbar,
  Alert,
  IconButton,
  LinearProgress,
  FormControlLabel,
  Switch,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  InputAdornment,
  Tabs,
  Tab,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/EditOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import LockResetIcon from '@mui/icons-material/LockReset';
import CloseIcon from '@mui/icons-material/Close';
import PeopleIcon from '@mui/icons-material/PeopleOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import SecurityIcon from '@mui/icons-material/Security';
import { useTranslation } from 'react-i18next';

import { DataTable, ConfirmDialog } from '../../components/shared';
import { useApi, useApiMutation } from '../../hooks/useApi';
import { useAuth } from '../../contexts/AuthContext';
import { validatePassword, isPasswordValid } from '../../utils/passwordValidation';
import UserPermissionsTab from '../../components/admin/UserPermissionsTab';

// =============================================================================
// AdminUsers Page
// =============================================================================
export default function AdminUsers() {
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();

  // API — fetch users list
  const { data: users, loading, error, refetch } = useApi('/users', {
    defaultData: [],
  });

  // Mutation hook
  const { mutate, loading: mutating } = useApiMutation();

  // ===================================================================
  // Add/Edit Dialog state
  // ===================================================================
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null); // null = add, object = edit
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    password2: '',
    full_name: '',
    email: '',
    is_admin: false,
    is_super_admin: false,
    view_restriction: false,
  });
  const [formError, setFormError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showPassword2, setShowPassword2] = useState(false);
  const [dialogTab, setDialogTab] = useState(0); // 0=details, 1=permissions

  // Delete confirm state
  const [deleteUser, setDeleteUser] = useState(null);

  // Reset password dialog state
  const [resetUser, setResetUser] = useState(null);
  const [resetPassword, setResetPassword] = useState('');
  const [resetPassword2, setResetPassword2] = useState('');
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [showResetPassword2, setShowResetPassword2] = useState(false);
  const [resetError, setResetError] = useState('');

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // ===================================================================
  // Form helpers
  // ===================================================================
  const updateField = useCallback((field, value) => {
    setFormData((prev) => {
      const updated = { ...prev, [field]: value };
      // If is_super_admin turned on → force is_admin on
      if (field === 'is_super_admin' && value) {
        updated.is_admin = true;
      }
      // If is_admin turned off → force is_super_admin off
      if (field === 'is_admin' && !value) {
        updated.is_super_admin = false;
      }
      return updated;
    });
    setFormError('');
  }, []);

  // Password validation for create
  const passwordValidation = useMemo(
    () => formData.password ? validatePassword(formData.password) : [],
    [formData.password]
  );
  const passwordIsValid = useMemo(
    () => formData.password ? isPasswordValid(formData.password) : false,
    [formData.password]
  );

  // ===================================================================
  // Dialog handlers
  // ===================================================================
  const handleOpenAdd = useCallback(() => {
    setEditingUser(null);
    setFormData({
      username: '',
      password: '',
      password2: '',
      full_name: '',
      email: '',
      is_admin: false,
      is_super_admin: false,
      view_restriction: false,
    });
    setFormError('');
    setShowPassword(false);
    setShowPassword2(false);
    setDialogTab(0);
    setDialogOpen(true);
  }, []);

  const handleOpenEdit = useCallback((user) => {
    setEditingUser(user);
    setFormData({
      username: user.username || '',
      password: '',   // not sent on edit
      password2: '',  // not sent on edit
      full_name: user.full_name || '',
      email: user.email || '',
      is_admin: !!user.is_admin,
      is_super_admin: !!user.is_super_admin,
      view_restriction: !!user.view_restriction,
    });
    setFormError('');
    setDialogTab(0);
    setDialogOpen(true);
  }, []);

  const handleCloseDialog = useCallback(() => {
    if (mutating) return;
    setDialogOpen(false);
    setEditingUser(null);
  }, [mutating]);

  // ===================================================================
  // Save (create or update)
  // ===================================================================
  const handleSave = useCallback(async () => {
    if (mutating) return;

    const trimmedUsername = formData.username.trim();
    const trimmedFullName = formData.full_name.trim();

    // Validation
    if (!trimmedUsername) {
      setFormError(t('common.required') + ' — ' + t('users.username'));
      return;
    }
    if (!trimmedFullName) {
      setFormError(t('common.required') + ' — ' + t('users.fullName'));
      return;
    }

    // Password validation (create only)
    if (!editingUser) {
      if (!passwordIsValid) {
        setFormError(t('users.passwordValidation.minLength'));
        return;
      }
      // Super admin needs password2
      if (formData.is_super_admin) {
        if (!formData.password2) {
          setFormError(t('common.required') + ' — ' + t('users.password2'));
          return;
        }
        if (formData.password === formData.password2) {
          setFormError(t('users.passwordValidation.mustDiffer'));
          return;
        }
      }
    }

    setFormError('');

    let body;
    if (editingUser) {
      // Update — no password
      body = {
        username: trimmedUsername,
        full_name: trimmedFullName,
        email: formData.email.trim() || null,
        is_admin: formData.is_admin,
        is_super_admin: formData.is_super_admin,
        view_restriction: formData.view_restriction,
      };
    } else {
      // Create — with password
      body = {
        username: trimmedUsername,
        password: formData.password,
        full_name: trimmedFullName,
        email: formData.email.trim() || null,
        is_admin: formData.is_admin,
        is_super_admin: formData.is_super_admin,
        view_restriction: formData.view_restriction,
      };
      if (formData.is_super_admin && formData.password2) {
        body.password2 = formData.password2;
      }
    }

    let result;
    if (editingUser) {
      result = await mutate('put', `/users/${editingUser.id}`, body);
    } else {
      result = await mutate('post', '/users', body);
    }

    if (result.success) {
      setDialogOpen(false);
      setEditingUser(null);
      refetch();
      setSnackbar({
        open: true,
        message: result.message || t('common.success'),
        severity: 'success',
      });
    } else {
      setFormError(result.error || t('common.error'));
    }
  }, [formData, editingUser, mutating, mutate, passwordIsValid, refetch, t]);

  // ===================================================================
  // Delete
  // ===================================================================
  const handleDelete = useCallback(async () => {
    if (!deleteUser || mutating) return;

    const result = await mutate('delete', `/users/${deleteUser.id}`);

    if (result.success) {
      setDeleteUser(null);
      refetch();
      setSnackbar({ open: true, message: result.message || t('common.success'), severity: 'success' });
    } else {
      setDeleteUser(null);
      setSnackbar({ open: true, message: result.error || t('common.error'), severity: 'error' });
    }
  }, [deleteUser, mutating, mutate, refetch, t]);

  // ===================================================================
  // Reset Password
  // ===================================================================
  const handleOpenResetPassword = useCallback((user) => {
    setResetUser(user);
    setResetPassword('');
    setResetPassword2('');
    setResetError('');
    setShowResetPassword(false);
    setShowResetPassword2(false);
  }, []);

  const handleResetPassword = useCallback(async () => {
    if (!resetUser || mutating) return;

    if (!isPasswordValid(resetPassword)) {
      setResetError(t('users.passwordValidation.minLength'));
      return;
    }

    // Super admin target: require second password
    if (resetUser.is_super_admin) {
      if (!resetPassword2 || !isPasswordValid(resetPassword2)) {
        setResetError(t('common.required') + ' — ' + t('users.password2'));
        return;
      }
      if (resetPassword === resetPassword2) {
        setResetError(t('users.passwordValidation.mustDiffer'));
        return;
      }
    }

    setResetError('');
    const body = { new_password: resetPassword };
    if (resetUser.is_super_admin && resetPassword2) {
      body.new_password_secondary = resetPassword2;
    }

    const result = await mutate('post', `/users/${resetUser.id}/reset-password`, body);

    if (result.success) {
      setResetUser(null);
      setResetPassword('');
      setResetPassword2('');
      refetch();
      setSnackbar({ open: true, message: t('users.passwordReset'), severity: 'success' });
    } else {
      setResetError(result.error || t('common.error'));
    }
  }, [resetUser, resetPassword, resetPassword2, mutating, mutate, refetch, t]);

  // ===================================================================
  // Table config
  // ===================================================================
  const columns = useMemo(() => [
    {
      key: 'username',
      label: t('users.username'),
      sortable: true,
    },
    {
      key: 'full_name',
      label: t('users.fullName'),
      sortable: true,
    },
    {
      key: 'is_admin',
      label: t('users.role'),
      sortable: true,
      width: 130,
      render: (val, row) => {
        if (row.is_super_admin) return <Chip label={t('users.isSuperAdmin')} color="error" size="small" />;
        if (val) return <Chip label={t('users.isAdmin')} color="primary" size="small" />;
        return <Chip label={t('users.role')} variant="outlined" size="small" />;
      },
    },
    {
      key: 'active',
      label: t('common.status'),
      sortable: true,
      width: 100,
      render: (val) => (
        <Chip
          label={val ? t('common.active') : t('common.inactive')}
          color={val ? 'success' : 'default'}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      key: 'using_default_password',
      label: t('users.usingDefaultPassword'),
      sortable: true,
      width: 80,
      render: (val) => val ? (
        <WarningAmberIcon fontSize="small" color="warning" titleAccess={t('users.usingDefaultPassword')} />
      ) : null,
    },
    {
      key: 'last_login',
      label: t('users.lastLogin'),
      sortable: true,
      width: 160,
      render: (val) => val
        ? new Date(val).toLocaleString('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
        : t('users.neverLoggedIn'),
    },
  ], [t]);

  const actions = useMemo(() => [
    {
      key: 'edit',
      icon: <EditIcon fontSize="small" />,
      label: t('common.edit'),
      onClick: handleOpenEdit,
    },
    {
      key: 'resetPassword',
      icon: <LockResetIcon fontSize="small" />,
      label: t('users.resetPassword'),
      onClick: handleOpenResetPassword,
    },
    {
      key: 'delete',
      icon: <DeleteIcon fontSize="small" />,
      label: t('common.delete'),
      color: 'error',
      onClick: (user) => setDeleteUser(user),
      // Cannot delete yourself
      show: (row) => row.id !== currentUser?.id,
    },
  ], [t, handleOpenEdit, handleOpenResetPassword, currentUser]);

  const toolbar = useMemo(() => (
    <Button
      data-testid="users__button--add"
      variant="contained"
      startIcon={<AddIcon />}
      onClick={handleOpenAdd}
      size="small"
    >
      {t('users.addUser')}
    </Button>
  ), [t, handleOpenAdd]);

  // ===================================================================
  // Password visibility toggle helper
  // ===================================================================
  const visibilityAdornment = (show, setShow) => (
    <InputAdornment position="end">
      <IconButton
        size="small"
        onClick={() => setShow((p) => !p)}
        edge="end"
        aria-label={show ? t('common.hidePassword') : t('common.showPassword')}
      >
        {show ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
      </IconButton>
    </InputAdornment>
  );

  // ===================================================================
  // Render
  // ===================================================================
  return (
    <Box data-testid="users__page" sx={{ pb: 4 }}>
      {/* Page title */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <PeopleIcon color="primary" />
        <Typography variant="h5" fontWeight={600}>
          {t('users.title')}
        </Typography>
      </Box>

      {/* Error */}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Table */}
      <DataTable
        testId="users__table"
        columns={columns}
        rows={users || []}
        actions={actions}
        loading={loading}
        searchable
        toolbar={toolbar}
        defaultSortKey="full_name"
        emptyStateProps={{
          title: t('users.noUsers'),
          message: t('users.noUsersMessage'),
          actionLabel: t('users.addUser'),
          onAction: handleOpenAdd,
        }}
      />

      {/* ============================================================= */}
      {/* Add/Edit Dialog                                                */}
      {/* ============================================================= */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        data-testid="users__dialog--edit"
      >
        {mutating && <LinearProgress />}

        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {editingUser ? t('users.editUser') : t('users.addUser')}
          <IconButton aria-label={t('common.close')} size="small" onClick={handleCloseDialog} disabled={mutating}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        {/* Tabs — only when editing (add has no permissions tab) */}
        {editingUser && (
          <Tabs
            value={dialogTab}
            onChange={(_, v) => setDialogTab(v)}
            data-testid="users__dialog--edit__tabs"
            sx={{ px: 2, borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label={t('common.details')} data-testid="users__dialog--edit__tab--details" />
            <Tab
              label={t('users.permissions.title')}
              icon={<SecurityIcon fontSize="small" />}
              iconPosition="start"
              data-testid="users__dialog--edit__tab--permissions"
            />
          </Tabs>
        )}

        <DialogContent dividers={!editingUser}>
          {/* Tab 0: Details (always shown for add, conditional for edit) */}
          {(dialogTab === 0 || !editingUser) && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {/* Username */}
            <TextField
              data-testid="users__dialog--edit__input--username"
              label={t('users.username')}
              value={formData.username}
              onChange={(e) => updateField('username', e.target.value)}
              fullWidth
              autoFocus
              required
              disabled={mutating}
              error={!!formError && formError.includes(t('users.username'))}
            />

            {/* Full name */}
            <TextField
              data-testid="users__dialog--edit__input--full-name"
              label={t('users.fullName')}
              value={formData.full_name}
              onChange={(e) => updateField('full_name', e.target.value)}
              fullWidth
              required
              disabled={mutating}
              error={!!formError && formError.includes(t('users.fullName'))}
            />

            {/* Email */}
            <TextField
              data-testid="users__dialog--edit__input--email"
              label={t('users.email')}
              value={formData.email}
              onChange={(e) => updateField('email', e.target.value)}
              fullWidth
              type="email"
              disabled={mutating}
            />

            {/* Password — CREATE only */}
            {!editingUser && (
              <>
                <Divider sx={{ my: 0.5 }} />

                <TextField
                  data-testid="users__dialog--edit__input--password"
                  label={t('users.password')}
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => updateField('password', e.target.value)}
                  fullWidth
                  required
                  disabled={mutating}
                  InputProps={{ endAdornment: visibilityAdornment(showPassword, setShowPassword) }}
                />

                {/* Validation rules */}
                {formData.password && (
                  <List dense disablePadding sx={{ ml: 1 }}>
                    {passwordValidation.map((rule) => (
                      <ListItem key={rule.key} disableGutters sx={{ py: 0 }}>
                        <ListItemIcon sx={{ minWidth: 28 }}>
                          {rule.passed
                            ? <CheckCircleIcon fontSize="small" color="success" />
                            : <CancelIcon fontSize="small" color="error" />
                          }
                        </ListItemIcon>
                        <ListItemText
                          primary={t(`users.passwordValidation.${rule.key}`)}
                          primaryTypographyProps={{
                            fontSize: '0.8rem',
                            color: rule.passed ? 'success.main' : 'error.main',
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                )}

                {/* Password 2 — Super Admin only */}
                {formData.is_super_admin && (
                  <>
                    <TextField
                      data-testid="users__dialog--edit__input--password2"
                      label={t('users.password2')}
                      type={showPassword2 ? 'text' : 'password'}
                      value={formData.password2}
                      onChange={(e) => updateField('password2', e.target.value)}
                      fullWidth
                      required
                      disabled={mutating}
                      InputProps={{ endAdornment: visibilityAdornment(showPassword2, setShowPassword2) }}
                    />

                    {formData.password && formData.password2 && formData.password === formData.password2 && (
                      <Alert severity="error" variant="outlined" sx={{ py: 0 }}>
                        {t('users.passwordValidation.mustDiffer')}
                      </Alert>
                    )}
                  </>
                )}
              </>
            )}

            <Divider sx={{ my: 0.5 }} />

            {/* Role switches */}
            <FormControlLabel
              data-testid="users__dialog--edit__switch--is-admin"
              control={
                <Switch
                  checked={formData.is_admin}
                  onChange={(e) => updateField('is_admin', e.target.checked)}
                  disabled={mutating || formData.is_super_admin}
                />
              }
              label={t('users.isAdmin')}
            />

            <FormControlLabel
              data-testid="users__dialog--edit__switch--is-super-admin"
              control={
                <Switch
                  checked={formData.is_super_admin}
                  onChange={(e) => updateField('is_super_admin', e.target.checked)}
                  disabled={mutating}
                />
              }
              label={t('users.isSuperAdmin')}
            />

            <FormControlLabel
              data-testid="users__dialog--edit__switch--view-restriction"
              control={
                <Switch
                  checked={formData.view_restriction}
                  onChange={(e) => updateField('view_restriction', e.target.checked)}
                  disabled={mutating}
                />
              }
              label={
                <Box>
                  <Typography variant="body2">{t('users.viewRestriction')}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('users.viewRestrictionHelp')}
                  </Typography>
                </Box>
              }
            />

            {/* Form error */}
            {formError && (
              <Alert severity="error" sx={{ mt: 1 }}>{formError}</Alert>
            )}
          </Box>
          )}

          {/* Tab 1: Permissions (only when editing) */}
          {editingUser && dialogTab === 1 && (
            <UserPermissionsTab
              userId={editingUser.id}
              onSaved={refetch}
            />
          )}
        </DialogContent>

        {/* DialogActions — only show save for details tab */}
        {(dialogTab === 0 || !editingUser) && (
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button data-testid="users__dialog--edit__button--cancel" onClick={handleCloseDialog} disabled={mutating}>
            {t('common.cancel')}
          </Button>
          <Button
            data-testid="users__dialog--edit__button--save"
            variant="contained"
            onClick={handleSave}
            disabled={mutating || !formData.username.trim() || !formData.full_name.trim()
              || (!editingUser && !passwordIsValid)
              || (!editingUser && formData.is_super_admin && (!formData.password2 || formData.password === formData.password2))
            }
          >
            {mutating ? t('common.saving') : t('common.save')}
          </Button>
        </DialogActions>
        )}
      </Dialog>

      {/* ============================================================= */}
      {/* Delete Confirm Dialog                                          */}
      {/* ============================================================= */}
      <ConfirmDialog
        open={!!deleteUser}
        title={t('users.deleteUser')}
        message={t('users.deleteConfirm', { name: deleteUser?.full_name || deleteUser?.username })}
        onConfirm={handleDelete}
        onCancel={() => setDeleteUser(null)}
        confirmLabel={t('common.delete')}
        destructive
        loading={mutating}
      />

      {/* ============================================================= */}
      {/* Reset Password Dialog                                          */}
      {/* ============================================================= */}
      <Dialog
        open={!!resetUser}
        onClose={() => !mutating && setResetUser(null)}
        maxWidth="sm"
        fullWidth
        data-testid="users__dialog--reset-password"
      >
        {mutating && <LinearProgress />}

        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {t('users.resetPassword')} — {resetUser?.full_name || resetUser?.username}
          <IconButton aria-label={t('common.close')} size="small" onClick={() => setResetUser(null)} disabled={mutating}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              data-testid="users__dialog--reset-password__input--new-password"
              label={t('users.newPassword')}
              type={showResetPassword ? 'text' : 'password'}
              value={resetPassword}
              onChange={(e) => { setResetPassword(e.target.value); setResetError(''); }}
              onKeyDown={(e) => e.key === 'Enter' && handleResetPassword()}
              fullWidth
              autoFocus
              disabled={mutating}
              InputProps={{ endAdornment: visibilityAdornment(showResetPassword, setShowResetPassword) }}
            />

            {/* Validation rules */}
            {resetPassword && (
              <List dense disablePadding sx={{ ml: 1 }}>
                {validatePassword(resetPassword).map((rule) => (
                  <ListItem key={rule.key} disableGutters sx={{ py: 0 }}>
                    <ListItemIcon sx={{ minWidth: 28 }}>
                      {rule.passed
                        ? <CheckCircleIcon fontSize="small" color="success" />
                        : <CancelIcon fontSize="small" color="error" />
                      }
                    </ListItemIcon>
                    <ListItemText
                      primary={t(`users.passwordValidation.${rule.key}`)}
                      primaryTypographyProps={{
                        fontSize: '0.8rem',
                        color: rule.passed ? 'success.main' : 'error.main',
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            )}

            {/* Second password — super admin target only */}
            {resetUser?.is_super_admin && (
              <>
                <TextField
                  data-testid="users__dialog--reset-password__input--new-password-secondary"
                  label={t('users.password2')}
                  type={showResetPassword2 ? 'text' : 'password'}
                  value={resetPassword2}
                  onChange={(e) => { setResetPassword2(e.target.value); setResetError(''); }}
                  onKeyDown={(e) => e.key === 'Enter' && handleResetPassword()}
                  fullWidth
                  required
                  disabled={mutating}
                  InputProps={{ endAdornment: visibilityAdornment(showResetPassword2, setShowResetPassword2) }}
                />

                {resetPassword && resetPassword2 && resetPassword === resetPassword2 && (
                  <Alert severity="error" variant="outlined" sx={{ py: 0 }}>
                    {t('users.passwordValidation.mustDiffer')}
                  </Alert>
                )}
              </>
            )}

            {resetError && (
              <Alert severity="error">{resetError}</Alert>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button onClick={() => setResetUser(null)} disabled={mutating}>
            {t('common.cancel')}
          </Button>
          <Button
            data-testid="users__dialog--reset-password__button--save"
            variant="contained"
            onClick={handleResetPassword}
            disabled={mutating || !isPasswordValid(resetPassword)
              || (resetUser?.is_super_admin && (!resetPassword2 || !isPasswordValid(resetPassword2) || resetPassword === resetPassword2))
            }
          >
            {mutating ? t('common.saving') : t('users.resetPassword')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ============================================================= */}
      {/* Snackbar                                                       */}
      {/* ============================================================= */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
